// app/composables/useResourceGroupPicker.ts
//
// Resource-group picker normalization — shared by every map page that
// renders the "Group filter" dropdown (Dashboard, biDash/Map, VideoWall,
// Live).
//
// Why this exists (3.53.1, 2026-05-16):
//   Pre-3.53.1 each map page consumed `GET /kapi/live/map/options` (or
//   /kapi/map/options on VideoWall) and rendered `details.items` raw.
//   Only biDash applied an additional transform — drop
//   filterVisibility=internal, build a "Parent / Child" path label,
//   sort by the active locale. The result: operators reported "the
//   Dashboard group filter doesn't match Map and VideoWall"
//   (2026-05-16 bug report). Extracting the transform to one composable
//   makes the four picker surfaces agree on the group set + label
//   format. biDash remains the source-of-truth reference (its
//   `buildResourceGroupPickerOptions` logic was lifted verbatim).
//
// Endpoint unification is intentionally out of scope here: the BE
// /live/map/options and /map/options return identical data for an
// authenticated caller with X-Active-Org (both run through
// `filterCameraGroupOptionsForCaller` + `filterToRootGroups` per
// klynx-api docs/contracts/resource-group.md §5.8). The discrepancy
// users actually see is the FE-side transform, not the wire payload.

import type { ResourceGroupOption } from '~/types/devices'

export type ResourceGroupPickerOption = ResourceGroupOption & {
  label: string
  parentGroupId?: string | null
  filterVisibility?: string
  resourceType?: string
}

/**
 * Normalize raw `/kapi/(live/)map/options?type=camera` items into the
 * picker option list expected by Dashboard / biDash / VideoWall / Live.
 *
 * Rules:
 *   1. Drop items missing `id` or `name` (defensive — BE shouldn't emit
 *      these but the picker UI breaks badly if one slips through).
 *   2. Drop `filterVisibility === 'internal'`. These are admin-internal
 *      groups (e.g. system seeds) that BE returns for completeness;
 *      end-user pickers must not surface them.
 *   3. **Label = RG's own name only** (§5.9 FE companion, 2026-05-17).
 *      Pre-3.56 we built a "Parent / Child / Grandchild" breadcrumb by
 *      walking `parentGroupId` so siblings under different parents could
 *      be told apart; operators reported the breadcrumb got too long on
 *      deep trees and the leaf name is what they care about. With the
 *      admin pickers now flat (4 pages call `?root=false` to get
 *      everything via the §5.8 owner pass-through), the leaf-only label
 *      is the readable choice. Siblings under different parents with the
 *      same name remain disambiguated by `id` in the model — the picker
 *      stores `id` as the selection value, not the label.
 *   4. Sort by `localeCompare` using `'th'` when the user's active
 *      locale is Thai, else `'en'`. Falls back to lexicographic on
 *      unsupported locales — no error path.
 *
 * Pure function. SSR-safe. No reactivity — callers wrap into a `ref`
 * or `computed` themselves.
 */
export function buildResourceGroupPickerOptions(
  rawItems: readonly ResourceGroupOption[] | null | undefined,
  locale: 'th' | 'en' | string = 'en'
): ResourceGroupPickerOption[] {
  const items = Array.isArray(rawItems) ? rawItems : []
  const sortLocale = locale === 'th' ? 'th' : 'en'
  return items
    .filter((item): item is ResourceGroupOption => Boolean(item?.id && item?.name))
    .filter(item => item.filterVisibility !== 'internal')
    .map<ResourceGroupPickerOption>(item => ({
      ...item,
      parentGroupId: item.parentGroupId ?? null,
      label: item.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label, sortLocale))
}
